import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Spin, Row, Col, Space, Radio } from 'antd';
import { useQuery, useMutation } from 'react-query';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { antibodyService, AntibodyAnalysisTask, BatchCDRInput, BatchCDRAnalysisResult } from '@/api/antibodyService';
import ResultDisplay from '@/components/common/ResultDisplay';
import { isMultiFastaFormat, isSingleFastaFormat, extractSingleFastaSequence } from '@/utils/sequenceUtils';
import { pollTaskResult } from '@/api/sequenceService';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

export default function AntibodySequencePage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [results, setResults] = useState<any[]>([]);
  const [mergedResults, setMergedResults] = useState<any[]>([]);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState<boolean>(false);
  const [isPollingResult, setIsPollingResult] = useState<boolean>(false);
  const [originalFastaInput, setOriginalFastaInput] = useState<string>(''); // 存储原始FASTA输入

  // 获取可用任务列表
  const { data: tasks, isLoading: isLoadingTasks } = useQuery(
    'antibodyAnalysisTasks',
    () => antibodyService.getAnalysisTasks(),
    {
      onError: (error: any) => {
        message.error('加载分析任务失败: ' + error.message);
      }
    }
  );

  // 在组件作用域定义numberingTask，避免未定义错误
  const numberingTask = tasks?.find(task => task.name.includes('抗体编号') || task.module_id === 'findcdr');

  // 执行任务
  const { mutate: executeTask, isLoading: isExecutingTask } = useMutation(
    (data: { taskType: string; handlerName: string; params: any; taskId: string }) => 
      antibodyService.executeAnalysisTask(data.taskType, data.handlerName, data.params),
    {
      onSuccess: async (data, variables) => {
        await handleTaskSuccess(data, variables);
      },
      onError: (error: any) => {
        message.error('执行任务失败: ' + error.message);
      }
    }
  );

  // 批量执行任务
  const { mutate: executeBatchTask, isLoading: isExecutingBatchTask } = useMutation(
    (data: BatchCDRInput) => antibodyService.batchPredictCDR(data),
    {
      onSuccess: async (data: BatchCDRAnalysisResult) => {
        await handleBatchTaskSuccess(data);
      },
      onError: (error: any) => {
        message.error('批量执行任务失败: ' + error.message);
        setIsBatchProcessing(false);
        setCurrentTaskId(null);
        setIsPollingResult(false);
      }
    }
  );

  const processSingleResult = useCallback((resultData: any, variables: any) => {
    const task = tasks?.find(t => variables.taskId.includes(t.module_id + t.endpoint));
    const taskName = task ? task.name : variables.taskId;
    const title = resultData?.name || variables.params?.name || `${taskName}`;

    const normalizedResult = {
      ...resultData,
      name: resultData?.name || title
    };

    const newResult = {
      data: normalizedResult,
      title,
      taskId: variables.taskId,
      timestamp: new Date().toISOString()
    };

    setResults(prev => [...prev, newResult]);
    setMergedResults(prev => [...prev, newResult]);
  }, [tasks]);

  const normalizeBatchResults = useCallback((batchResult: any) => {
    if (!batchResult) {
      throw new Error('批量分析结果为空');
    }

    if (batchResult.success === false) {
      throw new Error(batchResult.error || '批量分析失败');
    }

    let resultItems: any[] = [];

    if (Array.isArray(batchResult.results)) {
      resultItems = batchResult.results;
    } else if (batchResult.results && typeof batchResult.results === 'object') {
      resultItems = Object.entries(batchResult.results).map(([key, value]) => ({
        sequence_id: key,
        result: value
      }));
    }

    if (resultItems.length === 0) {
      throw new Error('批量分析结果为空');
    }

    const normalizedItems = resultItems.map((item, index) => {
      const resultData = item?.result || item?.data || item;
      const sequenceId = item?.sequence_id || item?.name || resultData?.name || `序列${index + 1}`;

      return {
        data: {
          ...resultData,
          name: resultData?.name || sequenceId
        },
        title: sequenceId,
        taskId: 'batch_antibody_numbering',
        timestamp: new Date().toISOString(),
        originalIndex: index,
        rawData: item
      };
    });

    setResults(normalizedItems);
    setMergedResults([{
      data: normalizedItems.map(item => item.data),
      title: '抗体编号结果',
      taskId: 'batch_antibody_numbering',
      timestamp: new Date().toISOString(),
      originalResults: normalizedItems,
      originalInput: originalFastaInput,
      summary: {
        total_sequences: batchResult.total_sequences ?? normalizedItems.length,
        successful_count: batchResult.successful_count ?? normalizedItems.length,
        failed_count: batchResult.failed_count ?? 0
      }
    }]);

    const successCount = batchResult.successful_count ?? batchResult.total_sequences ?? normalizedItems.length;
    return successCount;
  }, [originalFastaInput, setResults, setMergedResults]);

  // 处理任务成功
  const handleTaskSuccess = useCallback(async (data: any, variables: any) => {
    try {
      if (data?.task_id && !data?.sequence && !data?.regions) {
        message.success(`任务已提交，任务ID: ${data.task_id}`);
        setIsPollingResult(true);
        const result = await pollTaskResult(data.task_id);
        if (!result) {
          throw new Error('任务返回结果为空');
        }
        processSingleResult(result, variables);
        message.success('分析完成');
      } else {
        processSingleResult(data, variables);
        message.success('分析完成');
      }
    } catch (error: any) {
      console.error('处理抗体任务结果失败:', error);
      message.error('处理任务结果失败: ' + (error?.message || '未知错误'));
    } finally {
      setCurrentTaskId(null);
      setIsPollingResult(false);
    }
  }, [processSingleResult]);

  // 处理批量任务成功
  const handleBatchTaskSuccess = useCallback(async (batchResult: any) => {
    try {
      if (batchResult?.task_id) {
        message.success(`任务已提交，任务ID: ${batchResult.task_id}`);
        setIsPollingResult(true);
        const polledResult = await pollTaskResult(batchResult.task_id);
        const successCount = normalizeBatchResults(polledResult);
        message.success(`成功分析 ${successCount} 条序列`);
      } else {
        const successCount = normalizeBatchResults(batchResult);
        message.success(`成功分析 ${successCount} 条序列`);
      }
    } catch (error: any) {
      console.error('批量分析处理失败:', error);
      message.error('批量分析失败: ' + (error?.message || '未知错误'));
    } finally {
      setIsBatchProcessing(false);
      setCurrentTaskId(null);
      setIsPollingResult(false);
    }
  }, [normalizeBatchResults]);



  // 删除单个结果
  const handleDeleteResult = useCallback((index: number) => {
    setResults(prev => prev.filter((_, i) => i !== index));
    setMergedResults(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 清空结果
  const handleClearAllResults = useCallback(() => {
    setResults([]);
    setMergedResults([]);
  }, []);

  // 提交任务
  const handleTaskSubmit = useCallback((task?: AntibodyAnalysisTask) => {
    form.validateFields().then(values => {
      const taskId = task ? (task.module_id + task.endpoint) : 'findcdr/cdr_analyze_json';
      setCurrentTaskId(taskId);
      
      const sequence = values.sequence.trim();
      
      // 检查是否为多序列（FASTA格式或包含多个序列）
      if (isMultiFastaFormat(sequence)) {
        // 使用批量接口处理多序列FASTA
        setOriginalFastaInput(sequence);
        setIsBatchProcessing(true);
        
        const batchInput: BatchCDRInput = {
          fasta_text: sequence,
          scheme: values.options?.scheme || 'imgt',
          cdr_definition: values.options?.cdr_definition || values.options?.scheme || 'imgt'
        };
        
        executeBatchTask(batchInput);
      } else if (isSingleFastaFormat(sequence)) {
        const { sequence: extractedSequence, id: extractedId } = extractSingleFastaSequence(sequence);
        executeTask({ 
          taskType: 'antibody_cdr',
          handlerName: 'cdr_analyze_json',
          params: { 
            sequence: extractedSequence,
            name: extractedId.replace('>', '').trim(),
            ...values.options 
          },
          taskId
        });
      } else {
        executeTask({ 
          taskType: 'antibody_cdr',
          handlerName: 'cdr_analyze_json',
          params: { 
            sequence,
            name: values.name || `序列1`, // 使用用户输入的名称或默认名称
            ...values.options 
          },
          taskId
        });
      }
    }).catch(err => {
      console.error('表单验证失败:', err);
      message.error('请检查输入');
    });
  }, [form, executeTask, executeBatchTask]);



  // 导出CSV
  const handleExportAllCSV = useCallback(() => {
    try {
      // 获取抗体编号结果 - 支持新的批量结果格式
      let antibodyResults: any[] = [];
      
      // 检查是否有批量结果
      const batchResult = mergedResults.find(result => result.taskId === 'batch_antibody_numbering');
      if (batchResult && Array.isArray(batchResult.data)) {
        // 批量结果：将数据数组转换为导出格式
        antibodyResults = batchResult.data.map((data: any, index: number) => ({
          data: data,
          taskId: 'batch_antibody_numbering'
        }));
      } else {
        // 单个结果：使用旧的过滤逻辑
        antibodyResults = results.filter(result => 
          result.taskId.includes('cdr_analyze') || result.taskId.includes('findcdr') || result.taskId === 'batch_antibody_numbering'
        );
      }
      
      if (antibodyResults.length === 0) {
        message.info('没有可导出的抗体编号结果');
        return;
      }
      
      // 准备CSV表头
      const headers = [
        '序列ID',
        '序列',
        '链类型',
        '编号方案',
        'CDR定义',
        'FR1',
        'CDR1', 
        'FR2',
        'CDR2',
        'FR3', 
        'CDR3',
        'FR4',
        '物种',
        'V基因',
        'J基因'
      ];
      
      // 准备CSV行数据
      const rows: string[][] = [];
      
      antibodyResults.forEach((result, index) => {
        const data = result.data;
        const sequenceId = data.name || `序列${index + 1}`;
        
        const row = [
          sequenceId,
          data.sequence || '',
          data.chain_type || '',
          data.scheme || '',
          data.cdr_definition || '',
          data.regions?.fr1 || data.regions?.FR1 || '',
          data.regions?.cdr1 || data.regions?.CDR1 || '',
          data.regions?.fr2 || data.regions?.FR2 || '',
          data.regions?.cdr2 || data.regions?.CDR2 || '',
          data.regions?.fr3 || data.regions?.FR3 || '',
          data.regions?.cdr3 || data.regions?.CDR3 || '',
          data.regions?.fr4 || data.regions?.FR4 || '',
          data.species || '',
          data.v_gene || '',
          data.j_gene || ''
        ];
        
        rows.push(row);
      });
      
      // 创建CSV内容
      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
        .join('\n');
      
      // 创建并下载CSV文件
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `抗体编号结果_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success(`成功导出 ${rows.length} 条抗体编号结果`);
    } catch (error) {
      console.error('导出CSV文件失败:', error);
      message.error('导出CSV文件失败');
    }
  }, [results]);

  return (

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/antibody')}
            style={{ marginBottom: 16 }}
          >
            返回抗体分析
          </Button>
          
          <Typography>
            <Title level={2}>CDR查找</Title>
            <Paragraph>
              进行抗体序列编号、CDR识别，支持FASTA格式和纯序列格式输入。
            </Paragraph>
          </Typography>
        </div>

        <Card title="序列输入" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical">
            <Form.Item
              name="sequence"
              label="抗体序列"
              rules={[
                { required: true, message: '请输入抗体序列' },
                { min: 10, message: '序列长度至少为10个氨基酸' }
              ]}
            >
              <TextArea 
                rows={8} 
                placeholder="请输入抗体序列（支持FASTA格式和纯序列格式）\n\n示例：\n>抗体序列1\nEVQLVESGGGLVQPGGSLRLSCAASGFTFS...\n\n或直接输入序列：\nEVQLVESGGGLVQPGGSLRLSCAASGFTFS..."
                showCount
              />
            </Form.Item>
            
            <Form.Item
              name="name"
              label="序列名称（可选）"
            >
              <Input placeholder="为您的序列命名，便于识别结果" />
            </Form.Item>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={["options", "scheme"]}
                  label="编码方案"
                  initialValue="imgt"
                >
                  <Radio.Group>
                    <Radio.Button value="kabat">Kabat</Radio.Button>
                    <Radio.Button value="chothia">Chothia</Radio.Button>
                    <Radio.Button value="imgt">IMGT</Radio.Button>
                    <Radio.Button value="aho">AHo</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["options", "cdr_definition"]}
                  label="CDR定义方案"
                  initialValue="imgt"
                >
                  <Radio.Group>
                    <Radio.Button value="kabat">Kabat</Radio.Button>
                    <Radio.Button value="chothia">Chothia</Radio.Button>
                    <Radio.Button value="imgt">IMGT</Radio.Button>
                    <Radio.Button value="north">North</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Button
                type="primary"
                onClick={() => {
          handleTaskSubmit(numberingTask);
                }}
                loading={
                  numberingTask && numberingTask.module_id && numberingTask.endpoint
                    ? (isExecutingTask && currentTaskId === (numberingTask.module_id + numberingTask.endpoint)) || isExecutingBatchTask
                    : false
                }
                disabled={isExecutingTask || isExecutingBatchTask}
              >
                CDR查找
              </Button>
        </Card>






        {(isExecutingTask || isExecutingBatchTask || isPollingResult || isBatchProcessing) && (
          <div style={{ textAlign: 'center', margin: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {isPollingResult ? '任务已提交，正在获取结果...' : (isExecutingBatchTask || isBatchProcessing ? '正在批量处理中，请稍候...' : '正在处理中，请稍候...')}
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={3}>分析结果</Title>
              <Space>
                {mergedResults.some(result => 
                  result.taskId === 'batch_antibody_numbering' ||
                  result.taskId === 'antibody_numbering_/findcdr/cdr_analyze' || 
                  result.taskId === 'antibody_numbering'
                ) && (
                  <Button 
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportAllCSV}
                  >
                    导出结果
                  </Button>
                )}
                <Button type="primary" danger onClick={handleClearAllResults}>
                  清空所有结果
                </Button>
              </Space>
            </div>
            
            {mergedResults.map((result: any, index: number) => (
              <ResultDisplay 
                key={`${result.taskId}-${index}`}
                data={result.data} 
                title={result.title}
                taskId={result.taskId}
                multiResults={true}
                originalInput={result.originalInput} // 传递原始FASTA输入
                onDeleteResult={() => {
                  // 找到结果的索引并删除
                  const resultIndex = mergedResults.findIndex(r => r === result);
                  if (resultIndex !== -1) {
                    handleDeleteResult(resultIndex);
                  }
                }}
                resultIndex={index}
                onExportAllCSV={
                  (result.taskId === 'batch_antibody_numbering' ||
                   result.taskId === 'antibody_numbering_/findcdr/cdr_analyze' || 
                   result.taskId === 'antibody_numbering') ? 
                    handleExportAllCSV : undefined
                }
              />
            ))}
          </div>
        )}
      </div>

  );
}